import { ref, computed, watch } from 'vue';
import api from '@/services/api';
import Swal from 'sweetalert2';
import {
    parseISO,
    isValid as isValidDate,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    endOfDay,
    isSameDay,
    isWithinInterval
} from 'date-fns';

export function usePatientHistory(patientId, options = {}) {
    const loading = ref(false);
    const error = ref(null);
    const items = ref([]);
    const filter = ref(options.defaultFilter || 'all'); // 'all' | 'notes' | 'prescriptions' | 'documents'
    const perPage = options.perPage || 100;

    // Búsqueda flexible (full JSON)
    const searchQuery = ref('');
    const criteria = ref([]); // arreglo de tokens adicionales (futuras claves/campos)

    // Filtros de fecha
    const dateFilter = ref({
        type: 'all', // 'all' | 'exact' | 'week' | 'month' | 'range'
        exactDate: '',
        weekDate: '', // una fecha dentro de la semana
        monthInput: '', // formato 'YYYY-MM'
        startDate: '',
        endDate: ''
    });

    const mapItem = (raw, tipo) => {
        const created = raw.created_at || raw.created || raw.date || raw.updated_at || null;
        let titulo = '';
        let resumen = '';

        if (tipo === 'nota-medica') {
            titulo = raw.reason_for_visit || 'Nota médica';
            // Preferir texto corto si existe
            resumen = raw.soap ? String(raw.soap).replace(/<[^>]*>/g, '').slice(0, 180) : '';
        } else if (tipo === 'receta') {
            titulo = 'Receta médica';
            const meds = raw.medicines ? String(raw.medicines).replace(/<[^>]*>/g, '') : '';
            const indic = raw.indications ? String(raw.indications).replace(/<[^>]*>/g, '') : '';
            resumen = (meds || indic).slice(0, 180);
        } else if (tipo === 'documento') {
            titulo = 'Reporte médico';
            resumen = raw.report ? String(raw.report).replace(/<[^>]*>/g, '').slice(0, 180) : '';
        }

        return {
            id: raw.id,
            tipo,
            titulo,
            resumen,
            created_at: created,
            category: raw.category || 'general-cases', // Incluir categoría
            order: raw.order || null, // 👈 Incluir order del backend
            raw
        };
    };

    const fetchNotes = async (pid) => {
        const { data } = await api.get('/patient-medical-notes', {
            params: { patient_id: pid, page: 1, per_page: perPage }
        });
        const arr = Array.isArray(data.data) ? data.data : [];
        return arr.map(n => mapItem(n, 'nota-medica'));
    };

    const fetchPrescriptions = async (pid) => {
        const { data } = await api.get('/patient-medical-prescriptions', {
            params: { patient_id: pid, page: 1, per_page: perPage }
        });
        const arr = Array.isArray(data.data) ? data.data : [];
        return arr.map(p => mapItem(p, 'receta'));
    };

    const fetchDocuments = async (pid) => {
        const { data } = await api.get('/patient-documents', {
            params: { patient_id: pid, page: 1, per_page: perPage }
        });
        const arr = Array.isArray(data.data) ? data.data : [];
        return arr.map(d => mapItem(d, 'documento'));
    };

    const refresh = async () => {
        const pid = typeof patientId === 'function' ? patientId() : (patientId?.value ?? patientId);
        if (!pid) return;
        loading.value = true;
        error.value = null;
        try {
            const [notes, prescriptions, documents] = await Promise.all([
                fetchNotes(pid),
                fetchPrescriptions(pid),
                fetchDocuments(pid)
            ]);
            const merged = [...notes, ...prescriptions, ...documents];
            merged.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
            items.value = merged;
        } catch (e) {
            error.value = e;
        } finally {
            loading.value = false;
        }
    };

    const getDateSafe = (d) => {
        if (!d) return null;

        // Si es formato YYYY-MM-DD, crear fecha en hora local (no UTC)
        if (typeof d === 'string' && d.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = d.split('-').map(Number);
            const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
            return isValidDate(localDate) ? localDate : null;
        }

        // Para fechas con hora completa, usar parseISO
        const parsed = typeof d === 'string' ? parseISO(d) : new Date(d);
        const isValid = isValidDate(parsed);
        return isValid ? parsed : null;
    };

    const matchDate = (item) => {
        const created = getDateSafe(item.created_at);
        if (!created) return false;
        const df = dateFilter.value;

        switch (df.type) {
            case 'all':
                return true;
            case 'exact': {
                const target = getDateSafe(df.exactDate);
                return target ? isSameDay(created, target) : true;
            }
            case 'week': {
                const base = getDateSafe(df.weekDate);
                if (!base) return true;
                const start = startOfWeek(base, { weekStartsOn: 1 });
                const end = endOfWeek(base, { weekStartsOn: 1 });
                return isWithinInterval(created, { start, end });
            }
            case 'month': {
                if (!df.monthInput) return true;
                // monthInput: 'YYYY-MM'
                const [y, m] = df.monthInput.split('-').map((x) => parseInt(x, 10));
                if (!y || !m) return true;
                const start = startOfMonth(new Date(y, m - 1, 1));
                const end = endOfMonth(start);
                return isWithinInterval(created, { start, end });
            }
            case 'range': {
                const s = getDateSafe(df.startDate);
                const e = getDateSafe(df.endDate);

                if (s && e) {
                    // Usar el FINAL del día para endDate (23:59:59.999)
                    const endOfDayDate = endOfDay(e);
                    const match = isWithinInterval(created, { start: s, end: endOfDayDate });
                    return match;
                }
                if (s && !e) return created >= s;
                if (!s && e) return created <= endOfDay(e);
                return true;
            }
            default:
                return true;
        }
    };

    const normalize = (v) => (v == null ? '' : String(v));

    const deepIncludesAll = (obj, tokens) => {
        if (!tokens || tokens.length === 0) return true;
        const lowered = tokens.map((t) => String(t).toLowerCase()).filter((t) => t.length > 0);
        if (lowered.length === 0) return true;

        const stack = [obj];
        while (stack.length) {
            const current = stack.pop();
            if (current == null) continue;
            if (Array.isArray(current)) {
                for (let i = 0; i < current.length; i++) stack.push(current[i]);
            } else if (typeof current === 'object') {
                for (const k in current) stack.push(current[k]);
            } else {
                const str = normalize(current).toLowerCase();
                if (!str) continue;
                // Comprobación perezosa: si todas coinciden, retornamos
                if (lowered.every((t) => str.includes(t))) {
                    return true;
                }
            }
        }
        return false;
    };

    const filteredItems = computed(() => {
        let base = items.value;

        // filtro por tipo
        if (filter.value === 'notes') base = base.filter(i => i.tipo === 'nota-medica');
        else if (filter.value === 'prescriptions') base = base.filter(i => i.tipo === 'receta');
        else if (filter.value === 'documents') base = base.filter(i => i.tipo === 'documento');

        // filtro por fecha
        base = base.filter(matchDate);

        // búsqueda libre en JSON completo (raw y campos normalizados)
        const tokens = [searchQuery.value, ...criteria.value].filter(Boolean);
        if (tokens.length > 0) {
            base = base.filter((it) => deepIncludesAll({ ...it.raw, ...it }, tokens));
        }

        return base;
    });

    const setFilter = (newFilter) => {
        filter.value = newFilter;
    };

    const setSearchQuery = (q) => {
        searchQuery.value = q || '';
    };

    const setCriteria = (arr) => {
        if (!Array.isArray(arr)) {
            criteria.value = [];
            return;
        }
        criteria.value = arr.filter((x) => x != null && String(x).trim().length > 0);
    };

    const setDateFilter = (payload) => {
        const def = { type: 'all', exactDate: '', weekDate: '', monthInput: '', startDate: '', endDate: '' };
        dateFilter.value = { ...def, ...(payload || {}) };
    };

    const deleteItem = async (item) => {
        const endpoints = {
            'nota-medica': '/patient-medical-notes',
            'receta': '/patient-medical-prescriptions',
            'documento': '/patient-documents'
        };

        const tipoTexto = {
            'nota-medica': 'nota médica',
            'receta': 'receta médica',
            'documento': 'documento'
        };

        try {
            const result = await Swal.fire({
                title: '¿Estás seguro?',
                html: `¡No podrás revertir esto!<br>¿Deseas eliminar esta ${tipoTexto[item.tipo]}: <b>${item.titulo}</b>?`,
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: 'Cancelar',
                confirmButtonText: 'Sí, eliminar!',
                customClass: {
                    confirmButton: 'btn bg-gradient-success',
                    cancelButton: 'btn bg-gradient-danger'
                },
                buttonsStyling: false
            });

            if (result.isConfirmed) {
                const endpoint = endpoints[item.tipo];
                if (!endpoint) {
                    throw new Error(`Tipo de elemento desconocido: ${item.tipo}`);
                }

                // Eliminar del backend
                await api.delete(`${endpoint}/${item.id}`);

                // Eliminar del estado local
                items.value = items.value.filter(i => !(i.id === item.id && i.tipo === item.tipo));

                Swal.fire('Eliminado!', 'El registro ha sido eliminado.', 'success');
            }
        } catch (error) {
            console.error('Error al eliminar:', error);
            Swal.fire('Error', 'Hubo un problema al eliminar el registro.', 'error');
        }
    };

    watch(
        () => (typeof patientId === 'function' ? patientId() : patientId?.value),
        (pid) => {
            if (pid) refresh();
        },
        { immediate: true }
    );

    return {
        loading,
        error,
        items,
        filteredItems,
        filter,
        setFilter,
        searchQuery,
        setSearchQuery,
        criteria,
        setCriteria,
        dateFilter,
        setDateFilter,
        deleteItem,
        refresh
    };
}


